import { Button } from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useTranslation } from "react-i18next";
import { isWaitList } from "utils/booking";

interface CancelButtonProps {
    onClick: () => void;
    isLoading: boolean;
    trackingId?: string;
    contrastMode?: "default" | "onImage";
}

export default function CancelButton({
    onClick,
    isLoading,
    trackingId,
    contrastMode = "default",
}: CancelButtonProps) {
    const { t } = useTranslation();
    const { data: booking } = useWebBooking();

    if (!booking) {
        return null;
    }

    const isWaitListBooking = isWaitList(booking);
    const isInquiry = booking.anyNonCompleteInquirySubStatus();

    const getCancelButtonText = () => {
        if (isInquiry) {
            return t("bookingRequest.cancel");
        }
        if (isWaitListBooking) {
            return t("waitList.waitListBookingCancel");
        }
        return t("cancelBookingButton");
    };

    const isOnImage = contrastMode === "onImage";

    // Frosted-white values chosen to remain legible against the fixed rgba(0,0,0,0.48) dark overlay in layout.tsx
    const ON_IMAGE_BG = "rgba(255, 255, 255, 0.94)";
    const ON_IMAGE_BG_ACTIVE = "rgba(245, 245, 245, 0.96)";
    const ON_IMAGE_BG_HOVER = "rgba(255, 255, 255, 1)";
    const ON_IMAGE_BORDER = "1px solid rgba(255, 255, 255, 0.74)";
    const ON_IMAGE_COLOR = "rgba(20, 20, 20, 0.92)";

    const contrastProps = isOnImage
        ? {
              bg: ON_IMAGE_BG,
              border: ON_IMAGE_BORDER,
              color: ON_IMAGE_COLOR,
              _active: { bg: ON_IMAGE_BG_ACTIVE },
              _hover: { bg: ON_IMAGE_BG_HOVER },
          }
        : { color: "inherit" };

    return (
        <Button
            onClick={onClick}
            size="xl"
            variant={isOnImage ? "secondary" : "ghost"}
            isLoading={isLoading}
            alignSelf={{ base: "stretch", sm: "flex-end" }}
            w={{ base: "full", sm: "auto" }}
            data-track-click={trackingId}
            data-button-contrast={isOnImage ? "on-image" : "default"}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(contrastProps as any)}
        >
            {getCancelButtonText()}
        </Button>
    );
}
